import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AsyncPipe, NgClass, NgFor, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import { DropDownItem } from '../../../models/shared/stylesheet/drop-down-item';
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle } from '@ng-bootstrap/ng-bootstrap';
import { Options } from '@popperjs/core';
import { PopperUtils } from '../../../utils/popper-utils';

export interface DropDownGroup {
  label: string | null;
  items: DropDownItem[];
}

@Component({
  selector: 'app-dropdown-input',
  templateUrl: './dropdown-input.component.html',
  styleUrls: ['./dropdown-input.component.scss'],
  standalone: true,
  imports: [
    AsyncPipe,
    NgClass,
    NgFor,
    NgIf,
    NgSwitch,
    NgSwitchCase,
    NgSwitchDefault,
    NgbDropdown,
    NgbDropdownToggle,
    NgbDropdownMenu,
    NgbDropdownItem
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownInputComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.Default
})
export class DropdownInputComponent implements ControlValueAccessor, OnChanges {
  @Input() dropdowns: DropDownItem[] = [];
  @Input() placeholder: string = '';
  @Input() isMobile: boolean = false;
  @Input() disabled: boolean = false;
  @Input() clearable: boolean = false;
  @Input() inlineClearText: string | null = null;
  @Input() error: boolean = false;
  @Input() controlId: string | null = null;
  @Input() name: string | null = null;
  @Input() fixToAnchorWidth: boolean = false;
  // Class added to the wrapper ngbDropdown creates around the open menu in <body> — the only
  // styling hook consumers have once the menu is portaled out of their DOM subtree.
  @Input() menuClass: string | null = null;

  @Output() selection = new EventEmitter<DropDownItem>();
  @Output() inputFocused = new EventEmitter<void>();

  @ViewChild('dropdown') ngbDropdownRef!: NgbDropdown;
  @ViewChild('dropdownToggle') dropdownToggleRef!: ElementRef<HTMLButtonElement>;
  @ViewChild('dropdownMenu') dropdownMenuRef!: ElementRef<HTMLElement>;

  public popperOptions = (options: Partial<Options>): Partial<Options> => ({
    ...options,
    modifiers: [...(options.modifiers ?? []), PopperUtils.minWidth]
  });

  public popperOptionsFixed = (options: Partial<Options>): Partial<Options> => ({
    ...options,
    modifiers: [...(options.modifiers ?? []), PopperUtils.sameWidth]
  });

  private pendingValue: any = null;
  private _closedBySelection = false;

  public selectedItem$ = new BehaviorSubject<DropDownItem | null>(null);
  public isMobile$ = new BehaviorSubject<boolean>(false);

  // Presentational view of `dropdowns` collapsed into consecutive category groups. When no item
  // declares a `group`, this is a single group with a null label (rendered without a header).
  public groups: DropDownGroup[] = [];

  onChange = (_: any) => {};
  onTouched = () => {};

  writeValue(obj: any): void {
    this.pendingValue = obj;
    this.trySelectValue();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public trackByValue(index: number, item: DropDownItem): DropDownItem['value'] {
    return item.getSelectionValue();
  }

  public trackByGroupLabel(index: number, group: DropDownGroup): string {
    return group.label ?? `__ungrouped_${index}`;
  }

  private buildGroups(items: DropDownItem[]): DropDownGroup[] {
    return (items ?? []).reduce<DropDownGroup[]>((groups, item) => {
      const label = item.group ?? null;
      const lastGroup = groups[groups.length - 1];
      if (lastGroup && lastGroup.label === label) {
        lastGroup.items.push(item);
      } else {
        groups.push({ label, items: [item] });
      }
      return groups;
    }, []);
  }

  public onDropdownOpenChange(isOpen: boolean): void {
    if (isOpen) {
      this.inputFocused.emit();
      this.centerSelectedItemInMenu();
    } else if (this._closedBySelection) {
      this._closedBySelection = false;
      this.dropdownToggleRef?.nativeElement.focus();
    }
  }

  private centerSelectedItemInMenu(): void {
    setTimeout(() => {
      const menu = this.dropdownMenuRef?.nativeElement;
      const selected = menu?.querySelector<HTMLElement>('.dropdown-item.selected');
      if (!menu || !selected) return;
      menu.scrollTop = selected.offsetTop - (menu.clientHeight - selected.offsetHeight) / 2;
    });
  }

  public itemSelected(item: DropDownItem): void {
    if (item.getSelectionIsDisabled()) return;
    this._closedBySelection = true;
    this.selectedItem$.next(item);
    this.selection.emit(item);
    this.onChange(item.getSelectionValue());
    this.onTouched();
  }

  public resetInput(): void {
    this._closedBySelection = true;
    this.selectedItem$.next(null);
    this.onChange(null);
    this.onTouched();
  }

  public clearSelection(event: Event): void {
    event.stopPropagation();
    this.resetInput();
    this._closedBySelection = false;
  }

  public onSelectChanged(event: Event): void {
    const val = (event.target as HTMLSelectElement).value;
    const selected = this.dropdowns.find(d => String(d.getSelectionValue()) === val);
    this.selection.emit(selected);
    this.selectedItem$.next(selected ?? null);
    this.onChange(val);
    this.onTouched();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.isMobile) {
      this.isMobile$.next(this.isMobile);
      this.ngbDropdownRef?.close();
    }
    if (changes.dropdowns) {
      this.groups = this.buildGroups(this.dropdowns);
      this.selectedItem$.once(item => {
        if (item) {
          this.pendingValue = item.getSelectionValue();
        }
        this.trySelectValue();
      });
    }
  }

  private trySelectValue(): void {
    if (!this.dropdowns?.length || this.pendingValue === undefined) return;
    const match = this.dropdowns.find(d => d.getSelectionValue() === this.pendingValue);
    if (match) {
      this.selectedItem$.next(match);
    } else {
      this.selectedItem$.next(null);
    }
    setTimeout(() => (this.pendingValue = null), 0);
  }
}
